import { useEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import type { TimelineMarker } from "./useAudienceLayerHost";

/*
 * AI goals on the player's progress bar.
 *
 * The Audience Layer decides which goals there are (it loads them, follows the
 * game, and knows who reviews); this draws them where the fan scrubs, inside
 * Bitmovin's own seekbar, so they move, hide and resize with it. Hover shows
 * the goal; a click hands it back to the layer, which plays it and the goals
 * after it and then returns to the game, exactly as pressing its card does.
 *
 * Positioned against the player's own seekable window, read once a second:
 * while live that is the DVR window, which grows as the game goes on, and on
 * a recorded game it is the whole duration. The layer's times are already on
 * the timeline the player is showing.
 */

type PlayerLike = {
  isLive?: () => boolean;
  getCurrentTime?: (mode?: string) => number;
  getTimeShift?: () => number;
  getMaxTimeShift?: () => number;
  getDuration?: () => number;
} | null | undefined | "";

export interface SeekRange { startSec: number; endSec: number }

/** The span the seekbar covers, on the same timeline as the marks. */
export function seekRange(p: PlayerLike): SeekRange | null {
  if (!p) return null;
  if (p.isLive?.()) {
    const rel = p.getCurrentTime?.("relativetime");
    if (typeof rel !== "number" || !Number.isFinite(rel)) return null;
    // timeShift is how far behind the live edge the fan is (0 or negative),
    // maxTimeShift how far back the window reaches (negative).
    const edge = rel - (p.getTimeShift?.() ?? 0);
    const back = p.getMaxTimeShift?.() ?? 0;
    return back < 0 ? { startSec: edge + back, endSec: edge } : null;
  }
  const d = p.getDuration?.();
  return typeof d === "number" && Number.isFinite(d) && d > 0 ? { startSec: 0, endSec: d } : null;
}

/** Where on the bar a moment sits, 0 to 1, or null when it is outside the window. */
export function barFraction(atMs: number, range: SeekRange): number | null {
  const f = (atMs / 1000 - range.startSec) / (range.endSec - range.startSec);
  return f >= 0 && f <= 1 ? f : null;
}

const layerStyle: CSSProperties = {
  position: "absolute", inset: 0, pointerEvents: "none", zIndex: 3,
};

// The tenant's own brand colour (config.primary), so the marks read as this
// league's rather than as something laid over its player.
const FALLBACK_COLOR = "#0b4ea2";

function dotStyle(fraction: number, pending: boolean, active: boolean, color: string): CSSProperties {
  return {
    position: "absolute",
    left: `${fraction * 100}%`,
    top: "50%",
    width: active ? 14 : 11,
    height: active ? 14 : 11,
    transform: "translate(-50%, -50%)",
    borderRadius: "50%",
    boxSizing: "border-box",
    // Waiting for a reviewer: hollow and dashed, so it cannot pass for a goal fans see.
    background: pending ? "rgba(255,255,255,0.85)" : color,
    border: pending ? `2px dashed ${color}` : "2px solid #fff",
    boxShadow: "0 0 0 1px rgba(0,0,0,0.35)",
    pointerEvents: "auto",
    cursor: "pointer",
    transition: "width 120ms, height 120ms",
  };
}

function cardStyle(fraction: number): CSSProperties {
  return {
    position: "absolute",
    bottom: 18,
    left: `${fraction * 100}%`,
    // Kept on the bar at either end rather than centred off its edge.
    transform: `translateX(${fraction < 0.12 ? "-10%" : fraction > 0.88 ? "-90%" : "-50%"})`,
    width: 176,
    padding: 6,
    borderRadius: 8,
    background: "rgba(17,24,39,0.92)",
    color: "#fff",
    fontFamily: "inherit",
    pointerEvents: "none",
    boxShadow: "0 4px 14px rgba(0,0,0,0.35)",
  };
}

function Mark({ marker, fraction, onSelect, color }: {
  marker: TimelineMarker;
  fraction: number;
  onSelect: (id: string) => void;
  color: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState(false);
  const select = useRef(onSelect);
  select.current = onSelect;

  /*
   * Native listeners, not React's. The seekbar these sit in listens natively,
   * and a native event reaches it on the way up to React's root, so a React
   * stopPropagation would come too late: the press would scrub the video as
   * well as play the goal.
   */
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const swallow = (e: Event) => { e.stopPropagation(); e.preventDefault(); };
    // A touch only stops here: preventing its default would also cancel the
    // click the browser makes from it, which is what selects the goal.
    const stopTouch = (e: Event) => { e.stopPropagation(); };
    const click = (e: Event) => { swallow(e); select.current(marker.id); };
    el.addEventListener("mousedown", swallow);
    el.addEventListener("pointerdown", swallow);
    el.addEventListener("touchstart", stopTouch, { passive: true });
    el.addEventListener("click", click);
    return () => {
      el.removeEventListener("mousedown", swallow);
      el.removeEventListener("pointerdown", swallow);
      el.removeEventListener("touchstart", stopTouch);
      el.removeEventListener("click", click);
    };
  }, [marker.id]);

  return (
    <>
      <div
        ref={ref}
        role="button"
        tabIndex={0}
        aria-label={`Play AI goal: ${marker.title}${marker.detail ? `, ${marker.detail}` : ""}`}
        data-testid="ai-mark"
        style={dotStyle(fraction, marker.pending, hover, color)}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onFocus={() => setHover(true)}
        onBlur={() => setHover(false)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(marker.id); } }}
      />
      {hover && (
        <div style={cardStyle(fraction)}>
          {marker.thumbnailUrl && (
            <img
              src={marker.thumbnailUrl}
              alt=""
              style={{ width: "100%", aspectRatio: "16 / 9", objectFit: "cover", borderRadius: 5, display: "block" }}
            />
          )}
          <div style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 6 }}>
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.05em", color: "#fff", background: color, borderRadius: 4, padding: "1px 6px" }}>
              ✨ AI GOAL
            </span>
            {marker.pending && (
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.05em", color: "#fde68a" }}>NEEDS CHECK</span>
            )}
          </div>
          <div style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.3 }}>{marker.title}</div>
          {marker.detail && <div style={{ fontSize: 11, opacity: 0.8 }}>{marker.detail}</div>}
        </div>
      )}
    </>
  );
}

export default function AiTimelineMarkers({ playerRef, containerId, markers, onSelect, color }: {
  playerRef: { current: PlayerLike };
  /** The element the Bitmovin player was created in. */
  containerId: string | undefined;
  markers: TimelineMarker[];
  onSelect: (id: string) => void;
  /** The tenant's brand colour, config.primary. */
  color?: string;
}) {
  const [bar, setBar] = useState<HTMLElement | null>(null);
  const [range, setRange] = useState<SeekRange | null>(null);

  useEffect(() => {
    if (markers.length === 0 || !containerId) return;
    /*
     * The bar is found again each second rather than once: Bitmovin rebuilds
     * its UI when it switches variants (small screen, fullscreen, an ad), and
     * the marks have to follow it into the new one.
     */
    const read = () => {
      const el = document.getElementById(containerId)?.querySelector<HTMLElement>(".bmpui-ui-seekbar .bmpui-seekbar") ?? null;
      setBar((prev) => (prev === el ? prev : el));
      const next = seekRange(playerRef.current);
      setRange((prev) => (prev && next && Math.abs(prev.startSec - next.startSec) < 0.5
        && Math.abs(prev.endSec - next.endSec) < 0.5 ? prev : next));
    };
    read();
    const timer = window.setInterval(read, 1000);
    return () => window.clearInterval(timer);
  }, [markers.length, containerId, playerRef]);

  if (!bar || !range || markers.length === 0) return null;
  const placed = markers
    .map((m) => ({ m, f: barFraction(m.atMs, range) }))
    .filter((x): x is { m: TimelineMarker; f: number } => x.f !== null);
  if (placed.length === 0) return null;

  return createPortal(
    <div style={layerStyle} data-testid="ai-marks">
      {placed.map(({ m, f }) => (
        <Mark key={m.id} marker={m} fraction={f} onSelect={onSelect} color={color || FALLBACK_COLOR} />
      ))}
    </div>,
    bar,
  );
}
